// @ts-nocheck
import { useMemo } from 'react';
import axios from 'axios';

import {
  BASE_URL,
  useNewQueryEngine,
  useQueryEngine,
} from '../../../../../queries/useDataEngine';
import { EMPTY_OBJECT } from '../../../../../const/CommonConst';
import {
  getAttributeComments,
  getCustomEntityList,
  getOngoingProcesses,
  getIsEntityAvailable,
  getEntityTypeMetadata,
  getAttributeList,
  getAttributeListData,
  getEntitySectionPermissions,
  getSingleEntity,
  getTeamDetails,
  getGroupDetails,
  getSingleEntityDetails,
  getUserRolePermissions,
  getArtifactProcessStatus,
  getToolbarById,
  getModelPreviousState,
  getAlertEvents,
  getApprovalDetails,
  getEntityMetaData,
  getStateDetails,
  getEntityTypeCounts,
  getWorkflowCheckList,
  getShowInSummary,
  getVersions,
  getVersionsComparison,
  createVersion,
  rollbackVersion,
  getEntityStatus,
  getOverrideAttributeValue,
  getDraftAttributeValue,
  getActionables,
  normalizeDraftHistoryData,
  getHistory,
  getIssueTypesInfo,
  getWorkflowDrawerDetails,
  getIsCycleProcessEnabled,
  getCycleProcessDetails,
  getAttributeSourceMapping,
} from '../api/entityDetails.api';
import {
  ATTRIBUTE_COMMENTS_QUERY_KEY,
  HISTORY_QUERY_KEY,
  CUSTOM_ENTITY_LIST_QUERY_KEY,
  LINKED_ENTITIES_TABLE_QUERY_KEY,
  ONGOING_PROCESSES_QUERY_KEY,
  IS_ENTITY_AVAILABLE_QUERY_KEY,
  ENTITY_TYPE_METADATA_QUERY_KEY,
  STATE_DETAILS_QUERY_KEY,
  ENTITY_TYPE_COUNTS_QUERY_KEY,
  ATTRIBUTE_LIST_QUERY_KEY,
  ATTRIBUTE_LIST_DATA_QUERY_KEY,
  ENTITY_SECTION_PERMISSIONS_QUERY_KEY,
  SINGLE_ENTITY_QUERY_KEY,
  TEAM_DETAILS_QUERY_KEY,
  GROUP_DETAILS_QUERY_KEY,
  SINGLE_ENTITY_DETAILS_QUERY_KEY,
  USER_ROLE_PERMISSIONS_QUERY_KEY,
  ARTIFACT_PROCESS_STATUS_QUERY_KEY,
  TOOLBAR_BY_ID_QUERY_KEY,
  MODEL_PREVIOUS_STATE_QUERY_KEY,
  ALERT_EVENTS_QUERY_KEY,
  APPROVAL_DETAILS_QUERY_KEY,
  ENTITY_META_DATA_QUERY_KEY,
  WORKFLOW_CHECKLIST_QUERY_KEY,
  SUMMARY_SECTION_DETAILS_QUERY_KEY,
  GET_VERSIONS_QUERY_KEY,
  CREATE_VERSION_QUERY_KEY,
  ROLLBACK_VERSION_QUERY_KEY,
  GET_VERSIONS_COMPARISON_QUERY_KEY,
  ENTITY_STATUS_QUERY_KEY,
  OVERRIDE_ATTRIBUTE_VALUE_QUERY_KEY,
  DRAFT_ATTRIBUTE_VALUE_QUERY_KEY,
  ACTIONABLES_QUERY_KEY,
  WORKFLOW_DRAWER_DETAILS_QUERY_KEY,
  IS_CYCLE_PROCESS_ENABLED_QUERY_KEY,
  CYCLE_PROCESS_DETAILS_QUERY_KEY,
  ATTRIBUTE_SOURCE_MAPPING_QUERY_KEY,
} from '../constants/entityDetails.constants';

const COMMON_QUERY_OPTIONS = {
  retry: 1,
  staleTime: 5 * 60 * 1000,
  gcTime: 30 * 60 * 1000,
  select: (response) => response?.data,
  refetchOnWindowFocus: false,
  refetchOnReconnect: false,
};

const withParams = (queryKey, params) => {
  return [...queryKey, JSON.stringify(params || {})];
};

const EntityDetailsService = {
  getAllAvailableAttributes: (params, loader = false) =>
    axios.get(BASE_URL + '/custom-entity/get_available_attributes', {
      params,
      loader,
    }),
};

const ENTITY_KEYS = {
  ALL_AVAILABLE_ATTRIBUTES_KEYS: {
    ROOT: ['entityDetails', 'allAvailableAttributes'],
    key: (params = {}) => ['entityDetails', 'allAvailableAttributes', params],
  },
};

export const useIsEntityAvailableQuery = (params = {}, options = {}) => {
  const isEnabled = Boolean(options?.enabled);

  const queryKey = useMemo(() => {
    return withParams(IS_ENTITY_AVAILABLE_QUERY_KEY, params);
  }, [JSON.stringify(params)]);

  return useQueryEngine(queryKey, () => getIsEntityAvailable(params), {
    ...COMMON_QUERY_OPTIONS,
    ...options,
    enabled: isEnabled,
  });
};

export const useEntityTypeMetadataQuery = (
  { entity_id } = {},
  options = {}
) => {
  const isEnabled = Boolean(options?.enabled);

  const queryKey = useMemo(() => {
    return withParams(ENTITY_TYPE_METADATA_QUERY_KEY, { entity_id });
  }, [entity_id]);

  return useQueryEngine(queryKey, () => getEntityTypeMetadata({ entity_id }), {
    ...COMMON_QUERY_OPTIONS,
    ...options,
    enabled: isEnabled,
  });
};

export const useEntityMetaDataQuery = (params = {}, options = {}) => {
  const isEnabled = Boolean(options?.enabled);

  const queryKey = useMemo(() => {
    return withParams(ENTITY_META_DATA_QUERY_KEY, params);
  }, [JSON.stringify(params)]);

  return useQueryEngine(queryKey, () => getEntityMetaData(params), {
    ...COMMON_QUERY_OPTIONS,
    ...options,
    enabled: isEnabled,
  });
};

export const useStateDetailsQuery = (params = {}, options = {}) => {
  const isEnabled = Boolean(options?.enabled);

  const queryKey = useMemo(() => {
    return withParams(STATE_DETAILS_QUERY_KEY, params);
  }, [JSON.stringify(params)]);

  return useQueryEngine(queryKey, () => getStateDetails(params), {
    ...COMMON_QUERY_OPTIONS,
    ...options,
    enabled: isEnabled,
  });
};

export const useAttributeCommentsQuery = (params = {}, options = {}) => {
  const isEnabled = Boolean(options?.enabled);

  const queryKey = useMemo(() => {
    return withParams(ATTRIBUTE_COMMENTS_QUERY_KEY, params);
  }, [JSON.stringify(params)]);

  return useQueryEngine(queryKey, () => getAttributeComments(params), {
    ...COMMON_QUERY_OPTIONS,
    ...options,
    enabled: isEnabled,
  });
};

export const useEntitySectionPermissionsQuery = (params = {}, options = {}) => {
  const isEnabled = Boolean(options?.enabled);

  const queryKey = useMemo(() => {
    return withParams(ENTITY_SECTION_PERMISSIONS_QUERY_KEY, params);
  }, [JSON.stringify(params)]);

  return useQueryEngine(queryKey, () => getEntitySectionPermissions(params), {
    ...COMMON_QUERY_OPTIONS,
    ...options,
    enabled: isEnabled,
  });
};

export const useSingleEntityQuery = (params = {}, options = {}) => {
  const isEnabled = Boolean(options?.enabled);

  const queryKey = useMemo(() => {
    return withParams(SINGLE_ENTITY_QUERY_KEY, params);
  }, [JSON.stringify(params)]);

  return useQueryEngine(queryKey, () => getSingleEntity(params), {
    ...COMMON_QUERY_OPTIONS,
    ...options,
    enabled: isEnabled,
  });
};

export const useCustomEntityListQuery = (params = {}, options = {}) => {
  const isEnabled = Boolean(options?.enabled);

  const queryKey = useMemo(() => {
    return withParams(CUSTOM_ENTITY_LIST_QUERY_KEY, params);
  }, [JSON.stringify(params)]);

  return useQueryEngine(queryKey, () => getCustomEntityList(params), {
    ...COMMON_QUERY_OPTIONS,
    ...options,
    enabled: isEnabled,
  });
};

export const useTeamDetailsQuery = (params = {}, options = {}) => {
  return useNewQueryEngine(
    (queryParams = {}) => withParams(TEAM_DETAILS_QUERY_KEY, queryParams),
    getTeamDetails,
    params,
    options
  );
};

export const useGroupDetailsQuery = (params = {}, options = {}) => {
  const isEnabled = Boolean(options?.enabled);

  const queryKey = useMemo(() => {
    return withParams(GROUP_DETAILS_QUERY_KEY, params);
  }, [JSON.stringify(params)]);

  return useQueryEngine(queryKey, () => getGroupDetails(params), {
    ...COMMON_QUERY_OPTIONS,
    ...options,
    enabled: isEnabled,
  });
};

export const useSingleEntityDetailsQuery = (params = {}, options = {}) => {
  const isEnabled = Boolean(options?.enabled);

  const queryKey = useMemo(() => {
    return withParams(SINGLE_ENTITY_DETAILS_QUERY_KEY, params);
  }, [JSON.stringify(params)]);

  return useQueryEngine(queryKey, () => getSingleEntityDetails(params), {
    ...COMMON_QUERY_OPTIONS,
    ...options,
    enabled: isEnabled,
  });
};

export const useUserRolePermissionsQuery = (params = {}, options = {}) => {
  const isEnabled = Boolean(options?.enabled);

  const queryKey = useMemo(() => {
    return withParams(USER_ROLE_PERMISSIONS_QUERY_KEY, params);
  }, [JSON.stringify(params)]);

  return useQueryEngine(queryKey, () => getUserRolePermissions(params), {
    ...COMMON_QUERY_OPTIONS,
    ...options,
    enabled: isEnabled,
  });
};

export const useArtifactProcessStatusQuery = (params = {}, options = {}) => {
  const isEnabled = Boolean(options?.enabled);

  const queryKey = useMemo(() => {
    return withParams(ARTIFACT_PROCESS_STATUS_QUERY_KEY, params);
  }, [JSON.stringify(params)]);

  return useQueryEngine(queryKey, () => getArtifactProcessStatus(params), {
    ...COMMON_QUERY_OPTIONS,
    ...options,
    enabled: isEnabled,
  });
};

export const useToolbarByIdQuery = ({ entityId } = {}, options = {}) => {
  const isEnabled = Boolean(options?.enabled);

  const queryKey = useMemo(() => {
    return withParams(TOOLBAR_BY_ID_QUERY_KEY, { entityId });
  }, [entityId]);

  return useQueryEngine(queryKey, () => getToolbarById({ entityId }), {
    ...COMMON_QUERY_OPTIONS,
    ...options,
    enabled: isEnabled,
  });
};

export const useModelPreviousStateQuery = (params = {}, options = {}) => {
  const isEnabled = Boolean(options?.enabled);

  const queryKey = useMemo(() => {
    return withParams(MODEL_PREVIOUS_STATE_QUERY_KEY, params);
  }, [JSON.stringify(params)]);

  return useQueryEngine(queryKey, () => getModelPreviousState(params), {
    ...COMMON_QUERY_OPTIONS,
    ...options,
    enabled: isEnabled,
  });
};

export const useAlertEventsQuery = (params = {}, options = {}) => {
  const isEnabled = Boolean(options?.enabled);

  const queryKey = useMemo(() => {
    return withParams(ALERT_EVENTS_QUERY_KEY, params);
  }, [JSON.stringify(params)]);

  return useQueryEngine(queryKey, () => getAlertEvents(params), {
    ...COMMON_QUERY_OPTIONS,
    ...options,
    enabled: isEnabled,
  });
};

export const useApprovalDetailsQuery = (params = {}, options = {}) => {
  const isEnabled = Boolean(options?.enabled);

  const queryKey = useMemo(() => {
    return withParams(APPROVAL_DETAILS_QUERY_KEY, params);
  }, [JSON.stringify(params)]);

  return useQueryEngine(queryKey, () => getApprovalDetails(params), {
    ...COMMON_QUERY_OPTIONS,
    ...options,
    enabled: isEnabled,
  });
};

export const useWorkflowChecklistQuery = (params = {}, options = {}) => {
  const isEnabled = Boolean(options?.enabled);

  const queryKey = useMemo(() => {
    return withParams(WORKFLOW_CHECKLIST_QUERY_KEY, params);
  }, [JSON.stringify(params)]);

  return useQueryEngine(queryKey, () => getWorkflowCheckList(params), {
    ...COMMON_QUERY_OPTIONS,
    ...options,
    enabled: isEnabled,
  });
};

export const useEntityTypeCountsQuery = (params = {}, options = {}) => {
  const isEnabled = Boolean(options?.enabled);

  const queryKey = useMemo(() => {
    return withParams(ENTITY_TYPE_COUNTS_QUERY_KEY, params);
  }, [JSON.stringify(params)]);

  return useQueryEngine(queryKey, () => getEntityTypeCounts(params), {
    ...COMMON_QUERY_OPTIONS,
    ...options,
    enabled: isEnabled,
  });
};

export const useSummarySectionDetailsQuery = (params = {}, options = {}) => {
  const isEnabled = Boolean(options?.enabled);

  const queryKey = useMemo(() => {
    return withParams(SUMMARY_SECTION_DETAILS_QUERY_KEY, params);
  }, [JSON.stringify(params)]);

  return useQueryEngine(queryKey, () => getShowInSummary(params), {
    ...COMMON_QUERY_OPTIONS,
    ...options,
    enabled: isEnabled,
  });
};

export const useGetVersionsQuery = (params = {}, options = {}) => {
  const queryKey = useMemo(() => {
    return withParams(GET_VERSIONS_QUERY_KEY, params);
  }, [JSON.stringify(params)]);

  return useQueryEngine(queryKey, () => getVersions(params), {
    ...COMMON_QUERY_OPTIONS,
    ...options,
  });
};

export const useCreateVersionQuery = (params = {}, options = {}) => {
  const queryKey = useMemo(() => {
    return withParams(CREATE_VERSION_QUERY_KEY, params);
  }, [params]);

  return useQueryEngine(queryKey, () => createVersion(params), {
    ...COMMON_QUERY_OPTIONS,
    ...options,
  });
};

export const useVersionsComparisonQuery = (
  params = {},
  options = { version_ids: '' }
) => {
  const queryKey = useMemo(() => {
    return withParams(GET_VERSIONS_COMPARISON_QUERY_KEY, {
      ...params,
      version_ids: params?.version_ids || options?.version_ids || '',
    });
  }, [params, options?.version_ids]);

  return useQueryEngine(
    queryKey,
    () => getVersionsComparison(params, options),
    {
      ...COMMON_QUERY_OPTIONS,
    }
  );
};

export const useRollbackVersionQuery = (params = {}, options = {}) => {
  const queryKey = useMemo(() => {
    return withParams(ROLLBACK_VERSION_QUERY_KEY, params);
  }, [params]);

  return useQueryEngine(queryKey, () => rollbackVersion(params), {
    ...COMMON_QUERY_OPTIONS,
    ...options,
  });
};

export const useTempAttributeListQuery = (params = {}, options = {}) => {
  const isEnabled = Boolean(options?.enabled);

  const queryKey = useMemo(() => {
    return withParams(ATTRIBUTE_LIST_QUERY_KEY, params);
  }, [JSON.stringify(params)]);

  return useQueryEngine(queryKey, () => getAttributeList(params), {
    ...COMMON_QUERY_OPTIONS,
    ...options,
    enabled: isEnabled,
  });
};

export const useAttributeListDataQuery = (params = {}, options = {}) => {
  const isEnabled = Boolean(options?.enabled);

  const queryKey = useMemo(() => {
    return withParams(ATTRIBUTE_LIST_DATA_QUERY_KEY, params);
  }, [JSON.stringify(params)]);

  return useQueryEngine(queryKey, () => getAttributeListData(params), {
    ...COMMON_QUERY_OPTIONS,
    ...options,
    enabled: isEnabled,
  });
};

export const useDraftAttributeValueQuery = (params = {}, options = {}) => {
  const isEnabled =
    options?.enabled === undefined
      ? Boolean(params && Object.keys(params || {}).length > 0)
      : Boolean(options?.enabled);

  const queryKey = useMemo(() => {
    return withParams(DRAFT_ATTRIBUTE_VALUE_QUERY_KEY, params);
  }, [JSON.stringify(params)]);

  return useQueryEngine(queryKey, () => getDraftAttributeValue(params), {
    ...COMMON_QUERY_OPTIONS,
    ...options,
    enabled: isEnabled,
  });
};

export const useOverrideAttributeValueQuery = (params = {}, options = {}) => {
  const isEnabled =
    options?.enabled === undefined
      ? Boolean(params && Object.keys(params || {}).length > 0)
      : Boolean(options?.enabled);

  const queryKey = useMemo(() => {
    return withParams(OVERRIDE_ATTRIBUTE_VALUE_QUERY_KEY, params);
  }, [JSON.stringify(params)]);

  return useQueryEngine(queryKey, () => getOverrideAttributeValue(params), {
    ...COMMON_QUERY_OPTIONS,
    ...options,
    enabled: isEnabled,
  });
};

export const useEntityStatusQuery = (params = {}, options = {}) => {
  const isEnabled = Boolean(options?.enabled);

  const queryKey = useMemo(() => {
    return withParams(ENTITY_STATUS_QUERY_KEY, params);
  }, [JSON.stringify(params)]);

  return useQueryEngine(queryKey, () => getEntityStatus(params), {
    ...COMMON_QUERY_OPTIONS,
    ...options,
    enabled: isEnabled,
  });
};

export const useActionablesQuery = (params = {}, options = {}) => {
  const isEnabled = Boolean(options?.enabled);

  const queryKey = useMemo(() => {
    return withParams(ACTIONABLES_QUERY_KEY, params);
  }, [JSON.stringify(params)]);

  return useQueryEngine(queryKey, () => getActionables(params), {
    ...COMMON_QUERY_OPTIONS,
    ...options,
    enabled: isEnabled,
  });
};

export const useAllAvailableAttributes = ({
  params = EMPTY_OBJECT,
  options = EMPTY_OBJECT,
} = EMPTY_OBJECT) => {
  return useNewQueryEngine(
    ENTITY_KEYS.ALL_AVAILABLE_ATTRIBUTES_KEYS.key,
    EntityDetailsService.getAllAvailableAttributes,
    params,
    { ...options, staleTime: 0 }
  );
};

export const useIsCycleProcessEnabledQuery = (params = {}, options = {}) => {
  const isEnabled = Boolean(options?.enabled);

  const queryKey = useMemo(() => {
    return withParams(IS_CYCLE_PROCESS_ENABLED_QUERY_KEY, params);
  }, [JSON.stringify(params)]);

  return useQueryEngine(queryKey, () => getIsCycleProcessEnabled(params), {
    ...COMMON_QUERY_OPTIONS,
    ...options,
    enabled: isEnabled,
  });
};

export const useCycleProcessDetailsQuery = (params = {}, options = {}) => {
  const isEnabled = Boolean(options?.enabled);

  const queryKey = useMemo(() => {
    return withParams(CYCLE_PROCESS_DETAILS_QUERY_KEY, params);
  }, [JSON.stringify(params)]);

  return useQueryEngine(queryKey, () => getCycleProcessDetails(params), {
    ...COMMON_QUERY_OPTIONS,
    ...options,
    enabled: isEnabled,
  });
};

export const useLinkedEntitiesTableQuery = (params = {}, options = {}) => {
  const isEnabled = Boolean(options?.enabled);

  const queryKey = useMemo(() => {
    return withParams(LINKED_ENTITIES_TABLE_QUERY_KEY, params);
  }, [JSON.stringify(params)]);

  return useQueryEngine(queryKey, () => getIssueTypesInfo(params), {
    ...COMMON_QUERY_OPTIONS,
    ...options,
    enabled: isEnabled,
  });
};

export const useWorkflowDrawerDetailsQuery = (params = {}, options = {}) => {
  const isEnabled = Boolean(options?.enabled);

  const queryKey = useMemo(() => {
    return withParams(WORKFLOW_DRAWER_DETAILS_QUERY_KEY, params);
  }, [JSON.stringify(params)]);

  return useQueryEngine(queryKey, () => getWorkflowDrawerDetails(params), {
    ...COMMON_QUERY_OPTIONS,
    ...options,
    enabled: isEnabled,
  });
};

export const useOngoingProcessesQuery = (params = {}, options = {}) => {
  const isEnabled = Boolean(options?.enabled);

  const queryKey = useMemo(() => {
    return withParams(ONGOING_PROCESSES_QUERY_KEY, params);
  }, [JSON.stringify(params)]);

  return useQueryEngine(queryKey, () => getOngoingProcesses(params), {
    ...COMMON_QUERY_OPTIONS,
    ...options,
    enabled: isEnabled,
  });
};

export const useAttributeSourceMappingQuery = (params = {}, options = {}) => {
  const isEnabled = Boolean(options?.enabled);

  const queryKey = useMemo(() => {
    return withParams(ATTRIBUTE_SOURCE_MAPPING_QUERY_KEY, params);
  }, [JSON.stringify(params)]);

  return useQueryEngine(queryKey, () => getAttributeSourceMapping(params), {
    ...COMMON_QUERY_OPTIONS,
    ...options,
    enabled: isEnabled,
  });
};
//need to remvoe

export const useHistoryQuery = (params = {}, options = {}) => {
  const entityId = params?.entity_id ?? params?.entityId;
  const isEnabled =
    options?.enabled !== undefined
      ? Boolean(options.enabled)
      : Boolean(entityId);

  const queryKey = useMemo(() => {
    return withParams(HISTORY_QUERY_KEY, params);
  }, [JSON.stringify(params)]);

  return useQueryEngine(queryKey, () => getHistory(params), {
    ...COMMON_QUERY_OPTIONS,
    ...options,
    enabled: isEnabled,
    select: (response) => normalizeDraftHistoryData(response?.data),
  });
};
